import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import PushReminderToggle from './PushReminderToggle';

async function getGPS() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error('Your phone does not support location. Cannot clock in/out.'));
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => {
        // Surface the real reason so admin / cleaner knows why it failed.
        const msg = err.code === err.PERMISSION_DENIED
          ? 'Location permission is blocked. Please enable location for this app in your phone settings, then try again.'
          : err.code === err.POSITION_UNAVAILABLE
          ? 'Cannot find your location. Make sure GPS is on and you have signal, then try again.'
          : err.code === err.TIMEOUT
          ? 'Location took too long. Move outside or near a window and try again.'
          : 'Could not get your location. Please try again.';
        reject(new Error(msg));
      },
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 0 },
    );
  });
}

function formatElapsed(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const OC_SERVICES = [
  { key: 'DAILY OFFICE CLEAN', charged: 174, paid: 100, required: true },
  { key: 'MOP SHOWROOM',       charged: 50,  paid: 40,  required: false },
];

export default function OfficeCleaner({ user }) {
  const today = new Date().toISOString().slice(0, 10);

  const [clockEvent, setClockEvent] = useState(null);
  const [clockLoading, setClockLoading] = useState(false);
  const [clockMsg, setClockMsg] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  const [tasks, setTasks] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [services, setServices] = useState(['DAILY OFFICE CLEAN']);
  // Confirmation modal shown before clock-out — forces the cleaner to tick
  // off what she actually did so she doesn't forget MOP SHOWROOM (or
  // accidentally submit a day with no services at all).
  const [showOutConfirm, setShowOutConfirm] = useState(false);
  const [outServices, setOutServices] = useState(['DAILY OFFICE CLEAN']);

  useEffect(() => {
    loadStatus();
    api.getChecklistTasks().then(setTasks).catch(console.error);
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (clockEvent?.clock_in_at && !clockEvent?.clock_out_at) {
      const update = () => setElapsed(Math.floor((Date.now() - new Date(clockEvent.clock_in_at)) / 1000));
      update();
      timerRef.current = setInterval(update, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [clockEvent]);

  async function loadStatus() {
    const ev = await api.clockStatus().catch(() => null);
    setClockEvent(ev);
    if (ev) {
      setChecklist(ev.checklist || []);
      setServices(ev.services?.length ? ev.services : ['DAILY OFFICE CLEAN']);
    }
  }

  async function handleClockIn() {
    setClockLoading(true); setClockMsg('Getting your location...');
    try {
      const coords = await getGPS();
      const res = await api.clockIn(coords);
      setClockMsg('');
      await loadStatus();
    } catch (err) { setClockMsg(err.message); }
    finally { setClockLoading(false); }
  }

  // Step 1 of clock-out: open the confirmation modal. We DON'T submit yet —
  // the cleaner must explicitly confirm which services she did today.
  function handleClockOut() {
    setOutServices(services && services.length > 0 ? [...services] : ['DAILY OFFICE CLEAN']);
    setClockMsg('');
    setShowOutConfirm(true);
  }

  function toggleOutService(key) {
    setOutServices(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  // Step 2: actually submit the clock-out with the confirmed service list.
  async function confirmClockOut() {
    if (outServices.length === 0) {
      setClockMsg('Please select at least one service before clocking out.');
      return;
    }
    setClockLoading(true); setClockMsg('Getting your location...');
    try {
      const coords = await getGPS();
      await api.clockOut({ ...coords, services: outServices });
      setServices(outServices);
      setShowOutConfirm(false);
      setClockMsg('');
      await loadStatus();
    } catch (err) { setClockMsg(err.message); }
    finally { setClockLoading(false); }
  }

  async function toggleCheck(label) {
    const next = checklist.includes(label)
      ? checklist.filter(l => l !== label)
      : [...checklist, label];
    setChecklist(next);
    await api.updateChecklist(next).catch(console.error);
  }

  function toggleService(key) {
    if (key === 'DAILY OFFICE CLEAN') return; // always required
    setServices(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  const isClockedIn  = clockEvent?.clock_in_at && !clockEvent?.clock_out_at;
  const isClockedOut = clockEvent?.clock_out_at;
  const totalPay = services.reduce((s, k) => {
    const svc = OC_SERVICES.find(o => o.key === k);
    return s + (svc?.paid || 0);
  }, 0);

  const progressPct = tasks.length ? Math.round((checklist.length / tasks.length) * 100) : 0;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>
      <h2 style={{ marginTop: 24, marginBottom: 4 }}>My Day</h2>
      <p style={{ color: '#6b7280', margin: '0 0 20px', fontSize: 14 }}>
        {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>

      {/* Web push opt-in — clock-in reminder if Ana forgets in the morning */}
      <PushReminderToggle />

      {/* ── NOT CLOCKED IN ── */}
      {!isClockedIn && !isClockedOut && (
        <div className="card" style={{ marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>👋</div>
          <h3 style={{ margin: '0 0 8px' }}>Good morning, {user.name.split(' ')[0]}!</h3>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 20px' }}>
            Ready to start? Tap Clock In to begin your shift.
          </p>
          {clockMsg && <div className="alert alert-warning" style={{ marginBottom: 12 }}>{clockMsg}</div>}
          <button
            className="btn btn-accent clock-in-btn-mobile"
            onClick={handleClockIn}
            disabled={clockLoading}
            style={{ fontSize: 17, padding: '12px 36px', fontWeight: 700, background: '#059669', color: '#fff' }}
          >
            {clockLoading ? '📍 GPS...' : <><span style={{ fontSize: 28, lineHeight: 1 }}>▶</span><span>Clock In</span></>}
          </button>

          {/* Preview checklist */}
          {tasks.length > 0 && (
            <div style={{ textAlign: 'left', marginTop: 20, borderTop: '1px solid #f3f4f6', paddingTop: 16 }}>
              <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Today's Tasks
              </div>
              {tasks.map(t => (
                <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, color: '#6b7280', fontSize: 13 }}>
                  <span>☐</span> {t.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CLOCKED IN ── */}
      {isClockedIn && (
        <>
          {/* Timer card */}
          <div className="card" style={{ marginBottom: 16, background: 'linear-gradient(135deg, #1A1A1A 0%, #2a3a42 100%)', color: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#B8D4E0', marginBottom: 4 }}>Time on shift</div>
                <div className="clock-timer-mobile" style={{ fontFamily: 'monospace', fontSize: 40, fontWeight: 700, color: '#F5C518', letterSpacing: 2 }}>
                  {formatElapsed(elapsed)}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  Clocked in at {formatTime(clockEvent.clock_in_at)}
                </div>
                {clockEvent.address_in && (
                  <div style={{ fontSize: 12, color: '#B8D4E0', marginTop: 2 }}>
                    📍 {clockEvent.address_in}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Earnings today</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#4ade80' }}>${totalPay.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#374151' }}>Services Today</h4>
            {OC_SERVICES.map(svc => (
              <div
                key={svc.key}
                onClick={() => !svc.required && toggleService(svc.key)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', borderRadius: 8, marginBottom: 8, cursor: svc.required ? 'default' : 'pointer',
                  background: services.includes(svc.key) ? '#e8f2f7' : '#f9fafb',
                  border: `1.5px solid ${services.includes(svc.key) ? '#4A7A91' : '#e5e7eb'}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{services.includes(svc.key) ? '☑' : '☐'}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{svc.key}</div>
                    {svc.required && <div style={{ fontSize: 11, color: '#9ca3af' }}>Always included</div>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: '#16a34a' }}>${svc.paid}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Checklist */}
          {tasks.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 14 }}>Task Checklist</h4>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{checklist.length}/{tasks.length} done</span>
              </div>
              <div className="raise-bar-container" style={{ marginBottom: 12 }}>
                <div className="raise-bar-fill" style={{ width: `${progressPct}%` }} />
              </div>
              {tasks.map(t => (
                <div
                  key={t.id}
                  onClick={() => toggleCheck(t.label)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                    borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 20, color: checklist.includes(t.label) ? '#16a34a' : '#d1d5db' }}>
                    {checklist.includes(t.label) ? '✅' : '⬜'}
                  </span>
                  <span style={{
                    fontSize: 14,
                    textDecoration: checklist.includes(t.label) ? 'line-through' : 'none',
                    color: checklist.includes(t.label) ? '#9ca3af' : '#374151',
                  }}>
                    {t.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Clock Out */}
          {clockMsg && <div className="alert alert-warning">{clockMsg}</div>}
          <button
            className="btn btn-danger clock-out-btn-mobile"
            onClick={handleClockOut}
            disabled={clockLoading}
            style={{ width: '100%', justifyContent: 'center', fontSize: 16, padding: '12px' }}
          >
            {clockLoading ? '📍 Getting location...' : '■ Clock Out'}
          </button>
        </>
      )}

      {/* ── CLOCKED OUT ── */}
      {isClockedOut && (
        <div>
          <div className="card" style={{ marginBottom: 16, borderTop: '3px solid #F5C518' }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 32 }}>🎉</div>
              <h3 style={{ margin: '8px 0 4px' }}>Shift complete!</h3>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Great work today, {user.name.split(' ')[0]}!</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ background: '#f4f8fa', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Clock In</div>
                <div style={{ fontWeight: 700 }}>{formatTime(clockEvent.clock_in_at)}</div>
                {clockEvent.address_in && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📍 {clockEvent.address_in}</div>}
              </div>
              <div style={{ background: '#f4f8fa', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Clock Out</div>
                <div style={{ fontWeight: 700 }}>{formatTime(clockEvent.clock_out_at)}</div>
                {clockEvent.address_out && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📍 {clockEvent.address_out}</div>}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Hours worked</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#4A7A91' }}>
                  {clockEvent.hours_worked?.toFixed(2)}h
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Today's pay</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>
                  ${clockEvent.daily_cost?.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Services summary */}
          {clockEvent.services?.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Services Completed</h4>
              {clockEvent.services.map(svc => {
                const info = OC_SERVICES.find(o => o.key === svc);
                return (
                  <div key={svc} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
                    <span>✅ {svc}</span>
                    <span style={{ fontWeight: 600, color: '#16a34a' }}>${info?.paid || 0} pay</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Checklist summary */}
          {tasks.length > 0 && (
            <div className="card">
              <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>
                Checklist — {clockEvent.checklist?.length || 0}/{tasks.length} tasks completed
              </h4>
              {tasks.map(t => {
                const done = clockEvent.checklist?.includes(t.label);
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 14, color: done ? '#374151' : '#9ca3af' }}>
                    <span>{done ? '✅' : '⬜'}</span>
                    <span style={{ textDecoration: done ? 'none' : 'line-through' }}>{t.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Clock-out confirmation modal ── */}
      {showOutConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 22 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17, color: '#1A1A1A' }}>
              Before you clock out
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
              Tap each service you completed today. You must select at least one.
            </p>

            {OC_SERVICES.map(svc => {
              const sel = outServices.includes(svc.key);
              return (
                <div
                  key={svc.key}
                  onClick={() => toggleOutService(svc.key)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 14px', borderRadius: 8, marginBottom: 10, cursor: 'pointer',
                    background: sel ? '#e8f2f7' : '#f9fafb',
                    border: `1.5px solid ${sel ? '#4A7A91' : '#e5e7eb'}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>{sel ? '☑' : '☐'}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{svc.key}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>${svc.paid} pay · ${svc.charged} charged</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {clockMsg && (
              <div className="alert alert-warning" style={{ marginTop: 8, marginBottom: 8 }}>{clockMsg}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                onClick={() => { setShowOutConfirm(false); setClockMsg(''); }}
                disabled={clockLoading}
                style={{
                  flex: 1, padding: '12px', borderRadius: 8,
                  background: '#fff', border: '1.5px solid #e5e7eb',
                  color: '#374151', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClockOut}
                disabled={clockLoading || outServices.length === 0}
                style={{
                  flex: 2, padding: '12px', borderRadius: 8,
                  background: outServices.length === 0 ? '#9ca3af' : '#dc2626',
                  border: 'none', color: '#fff', fontWeight: 800, fontSize: 14,
                  cursor: clockLoading || outServices.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {clockLoading ? '📍 GPS…' : '■ Confirm Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
